import React, { useState, useMemo } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import moment from 'moment';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ── World-Class Theme Constants ──
const THEME = {
  primary: '#CC9900',      // Brand Gold
  secondary: '#344054',    // Soft Charcoal
  accent: '#7F56D9',       // Premium Purple
  success: '#12B76A',      // Vibrant Green
  warning: '#F79009',      // Amber
  error: '#F04438',        // Rose Red
  border: '#EAECF0',       // Light Border
  bgHover: '#F9FAFB',      // Subtle Gray
};

const CHART_PALETTE = [
  '#CC9900', // Gold (Primary)
  '#12B76A', // Emerald (Success)
  '#6366F1', // Indigo (Tech)
  '#06AED4', // Teal (Fresh)
  '#F79009', // Amber (Warm)
  '#7F56D9', // Purple (Premium)
];

type ChartType = 'bar' | 'line' | 'pie';

interface CommonGraphProps {
  data: any[];
  title: string;
  labelKey?: string;
  dataKeys: string[];
  loading?: boolean;
}

const CommonGraph: React.FC<CommonGraphProps> = ({
  data,
  title,
  labelKey = 'date',
  dataKeys,
  loading = false,
}) => {
  const [chartType, setChartType] = useState<ChartType>('bar');

  const formatNumber = (val: number) => {
    return new Intl.NumberFormat('en-GB', {
      notation: val > 10000 ? 'compact' : 'standard',
      maximumFractionDigits: 1,
    }).format(val);
  };

  const totals = useMemo(() => {
    return dataKeys.map(key => ({
      key,
      total: data?.reduce((acc, curr) => acc + (curr[key] || 0), 0) || 0
    }));
  }, [data, dataKeys]);

  const chartData = useMemo(() => {
    const labels = data?.map((item) =>
      labelKey === 'date' ? moment(item[labelKey]).format('MMM DD') : item[labelKey]
    );

    return {
      labels,
      datasets: dataKeys.map((key, index) => {
        // ── Format Label (totalPointsEarned -> Points Earned) ──
        const cleanLabel = key
          .replace(/^total/, '') // Remove 'total' prefix
          .replace(/([A-Z])/g, ' $1') // Add space before capital letters
          .trim() // Remove stray spaces
          .replace(/^\w/, (c) => c.toUpperCase()); // Capitalize first letter

        const color = CHART_PALETTE[index % CHART_PALETTE.length];
        const isPrimary = index === 0;

        return {
          label: cleanLabel,
          data: data?.map((item) => item[key]),
          backgroundColor: (context: any) => {
            if (chartType === 'pie') return CHART_PALETTE;
            if (chartType === 'line') {
              const { ctx, chartArea } = context.chart;
              if (!chartArea) return color;
              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, `${color}33`);
              gradient.addColorStop(1, `${color}00`);
              return gradient;
            }
            return chartType === 'bar' ? color : `${color}11`;
          },
          borderColor: color,
          borderWidth: chartType === 'line' ? 2.5 : chartType === 'bar' ? 0 : 2,
          borderRadius: 6,
          barThickness: 'flex',
          maxBarThickness: 12,
          pointBackgroundColor: '#FFF',
          pointBorderColor: color,
          pointBorderWidth: 2,
          pointRadius: chartType === 'line' ? 0 : 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: '#FFF',
          pointHoverBorderWidth: 2,
          tension: 0.4,
          fill: chartType === 'line',
        };
      }),
    };
  }, [data, labelKey, dataKeys, chartType]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: false,
          boxWidth: 10,
          boxHeight: 10,
          padding: 20,
          font: { size: 12, weight: '500', family: "Inter, system-ui, sans-serif" },
          color: '#475467',
        },
      },
      tooltip: {
        backgroundColor: '#344054', // Matches THEME.secondary
        padding: 16,
        titleMarginBottom: 10,
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 13 },
        footerFont: { size: 10 },
        usePointStyle: true,
        boxPadding: 8,
        cornerRadius: 12,
        callbacks: {
          label: (ctx: any) => ` ${ctx.dataset.label}: ${formatNumber(ctx.parsed.y ?? ctx.parsed)}`,
        },
      },
    },
    scales: chartType === 'pie' ? {} : {
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: '#F2F4F7', drawTicks: false },
        ticks: {
          color: '#667085',
          font: { size: 11 },
          callback: (value: any) => formatNumber(value),
          padding: 12
        },
      },
      x: {
        border: { display: false },
        grid: { display: false },
        ticks: { color: '#667085', font: { size: 11 }, padding: 12 },
      },
    },
  }), [chartType, dataKeys]);

  return (
    <div className="flex flex-col bg-white border border-gray-100 rounded-[24px] shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)] hover:shadow-[0px_12px_24px_-4px_rgba(16,24,40,0.08),0px_4px_8px_-2px_rgba(16,24,40,0.03)] transition-all duration-500 ease-in-out h-full">
      {/* Header Section */}
      <div className="px-6 pt-6 pb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-[#101828] font-bold text-base leading-tight tracking-tight">{title}</h3>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            {totals.map((t, idx) => {
              const cleanMetric = t.key
                .replace(/^total/, '')
                .replace(/([A-Z])/g, ' $1')
                .trim()
                .replace(/^\w/, (c) => c.toUpperCase());

              return (idx === 0 || dataKeys.length > 1) && (
                <span key={t.key} className="flex items-center gap-1.5 text-[11px] font-semibold text-[#475467] bg-gray-50/50 px-2.5 py-1 rounded-md border border-gray-100/50">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CHART_PALETTE[idx % CHART_PALETTE.length] }} />
                  <span className="text-[#111827]">{formatNumber(t.total)}</span>
                  <span className="text-gray-400 font-medium uppercase tracking-tight text-[10px]">{cleanMetric}</span>
                </span>
              );
            })}
          </div>
        </div>

        {/* Premium Toggle Control */}
        <div className="inline-flex items-center p-1 bg-[#F9FAFB] rounded-[14px] border border-[#F2F4F7]">
          {(['bar', 'line', 'pie'] as ChartType[]).map((type) => (
            <button
              key={type}
              onClick={() => setChartType(type)}
              className={`px-4 py-2 rounded-[10px] text-[12px] font-bold tracking-tight transition-all duration-300 ${chartType === type
                ? 'bg-white text-[#CC9900] shadow-sm ring-1 ring-black/[0.04]'
                : 'text-[#667085] hover:text-[#101828] hover:bg-white/50'
                }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Main Visualization Area */}
      <div className="flex-grow px-6 pb-6 relative flex items-center justify-center min-h-[320px]">
        {loading ? (
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 border-[3px] border-gray-100 border-t-[#CC9900] rounded-full animate-spin shadow-inner" />
            <p className="mt-4 text-[12px] font-bold text-gray-400 uppercase tracking-widest">Processing Data</p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-[#F9FAFB] rounded-full flex items-center justify-center mb-5 border border-gray-50">
              <svg className="w-8 h-8 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
            </div>
            <h4 className="text-sm font-bold text-[#101828]">No Activity Recorded</h4>
            <p className="mt-2 text-[11px] text-gray-400 font-medium text-center max-w-[200px]">Data will appear here once the first entries are recorded for this period.</p>
          </div>
        ) : (
          <div className="w-full h-full transform transition-opacity duration-700">
            {chartType === 'bar' && <Bar data={chartData} options={options as any} />}
            {chartType === 'line' && <Line data={chartData} options={options as any} />}
            {chartType === 'pie' && <Pie data={chartData} options={options as any} />}
          </div>
        )}
      </div>

    </div>
  );
};

export default React.memo(CommonGraph);
